import * as React from 'react';

import GoogleMap from 'common/components/core/map/BaseGoogleMap';
import MapWithFitToBoundFactory from 'common/components/core/map/MapWithFitToBounds';
import NumberUtils from 'common/utils/NumberUtils';
import LatLngUtils from 'common/utils/LatLngUtils';

import { Checkbox, CheckboxTypes } from 'common/components/core/checkbox';
import { FitToBoundStrategyEnum } from 'common/enums/MapEnum';
import { dashboardColors } from 'common/enums/ColorEnum';
import { memoizedImmutable } from 'common/utils/Memoizer';
import { LatLng } from 'common/models/internal/LatLng';
import { LocationMarker } from 'common/components/core/markers/LocationMarker';
import { NonGeocodedMarker } from 'common/components/core/markers/NonGeocodedMarker';
import { geocode } from 'common/middleware/api';
import { ApiError } from 'common/modules/api';

const MAP_OPTIONS = {
    gestureHandling: 'cooperative'
};

const MapWithFitToBound = MapWithFitToBoundFactory(GoogleMap);

class EntityMapWithGeocoding extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            autoDetectLatLng: true,
            mapDraggable: true
        };
    }

    componentDidUpdate(prevProps) {
        const { mapDraggable } = this.state;
        if (mapDraggable && prevProps.latLng !== this.props.latLng) {
            this.fitMapToBound(this.props.latLng);
        }
        if (prevProps.address !== this.props.address) {
            const { autoDetectLatLng } = this.state;
            if (autoDetectLatLng) {
                this.geocodeAddress();
            }
        }
    }

    onMapLoad = mapInstance => {
        this.map = mapInstance.map;
        this.forceUpdate();
    };

    onMapContainerLoad = mapContainer => {
        this.mapContainer = mapContainer;
        this.forceUpdate();
    };

    onMapWithFitToBoundLoad = mapWithFitToBound => {
        this.mapWithFitToBound = mapWithFitToBound;
    };

    onAutoDetectToggle = value => {
        this.setState({
            autoDetectLatLng: value
        });
    };

    onChildMouseUp = (hoverKey, childProps, { lat, lng }) => {
        if (!NumberUtils.isNumeric(lat) && !NumberUtils.isNumeric(lng)) {
            // If its not numeric, don't set it.
            // Helps prevent when user drags marker outside.
            return;
        }
        const { onChange } = this.props;
        onChange && onChange(LatLng.fromJS({ lat, lng }));
        this.setState({ mapDraggable: true });
    };

    onChildMouseDown = () => {
        this.setState({ mapDraggable: false });
    };

    onChildMouseMove = (hoverKey, childProps, { lat, lng }) => {
        const { onChange } = this.props;
        onChange && onChange(LatLng.fromJS({ lat, lng }));
        this.setState({ mapDraggable: false });
    };

    handleNonGeocodedMarkerDragDone = (_, { x, y }) => {
        // find the target latlng
        const targetLatLng = LatLngUtils.getPixelToLatLng(undefined, this.map, {
            x,
            y
        });
        const { onChange } = this.props;
        onChange && onChange(LatLng.fromJS(targetLatLng));
    };

    getBbBoxMap = () => {
        var element = this.map.getDiv();
        return element.getBoundingClientRect();
    };

    fitMapToBound = updatedLatLng => {
        const latLngs = updatedLatLng ? [updatedLatLng] : null;
        const bounds = LatLngUtils.getBoundsFromLatLngs(latLngs);

        if (
            updatedLatLng &&
            updatedLatLng.isValid() &&
            this.mapWithFitToBound
        ) {
            this.mapWithFitToBound
                .getWrappedInstance()
                .fitBoundsMapInstance(bounds);
        }
    };

    geocodeAddress = () => {
        const { autoDetectLatLng } = this.state;
        const { clientId, address } = this.props;
        const params = {
            clientId
        };
        if (!address) {
            return;
        }
        const body = {
            location: {
                address: address.toJS()
            }
        };

        geocode
            .post(params, body)
            .then(geocodedLocation => {
                const { address, onChange } = this.props;
                const currentAddress = address;
                if (currentAddress && !address.equals(currentAddress)) {
                    // Response is late! The address in the fields have changed.
                    return;
                }
                if (!autoDetectLatLng) {
                    // User no longer wants us to geocode. Forget it!
                    return;
                }

                const { geometry } = geocodedLocation;
                if (geometry && geometry.latLng) {
                    const latLng = LatLng.fromJS(geometry.latLng);
                    onChange && onChange(latLng);
                    this.fitMapToBound(latLng);
                }
            })
            .catch(error => {
                if (error instanceof ApiError) {
                    return;
                }
                throw error;
            });
    };

    @memoizedImmutable()
    renderMarker(latLng, map, mapContainer) {
        if (!map) {
            return [];
        }
        const { theme } = this.props;
        if (!latLng || !latLng.isValid()) {
            if (mapContainer) {
                return (
                    <NonGeocodedMarker
                        theme={theme}
                        map={map}
                        container={mapContainer}
                        bboxMap={this.getBbBoxMap}
                        markerIcon="circle"
                        color={dashboardColors.darkSkyBlue.name}
                        isEditable
                        isVisible
                        handleDragDone={this.handleNonGeocodedMarkerDragDone}
                        disableSpiderMarkerDrag={false}
                    />
                );
            }
            return null;
        }
        const { lat, lng } = latLng;
        return (
            <LocationMarker
                className={theme.marker}
                theme={theme}
                lat={Number(lat)}
                lng={Number(lng)}
                markerIcon={<div className={theme.circle} />}
            />
        );
    }

    render() {
        const {
            theme,
            defaultCountryCenter,
            labelInfo,
            latLng,
            t
        } = this.props;
        const { autoDetectLatLng, mapDraggable } = this.state;

        return (
            <div className={theme.fieldGroup}>
                <div className={theme.row}>
                    <span className={theme.label}>{t(labelInfo.label)}</span>
                    <Checkbox
                        className={theme.autoDetectCheckbox}
                        color={dashboardColors.darkSkyBlue.name}
                        theme={theme}
                        checkboxType={CheckboxTypes.Solid}
                        checked={autoDetectLatLng}
                        onChange={this.onAutoDetectToggle}
                        label={t(labelInfo.autoDetect)}
                        smallCheckbox
                    />
                </div>
                <div ref={this.onMapContainerLoad} className={theme.mapWrap}>
                    <MapWithFitToBound
                        ref={this.onMapWithFitToBoundLoad}
                        enableFitToBounds
                        defaultCenter={defaultCountryCenter}
                        fitToBoundStrategy={FitToBoundStrategyEnum.NEXT_RENDER}
                        onGoogleApiLoaded={this.onMapLoad}
                        hideAutoFocus
                        theme={theme}
                        tabIndex={-1}
                        options={MAP_OPTIONS}
                        onChildMouseDown={this.onChildMouseDown}
                        onChildMouseUp={this.onChildMouseUp}
                        onChildMouseMove={this.onChildMouseMove}
                        disableDrag={!mapDraggable}
                    >
                        {this.renderMarker(latLng, this.map, this.mapContainer)}
                    </MapWithFitToBound>
                </div>
            </div>
        );
    }
}

export default EntityMapWithGeocoding;
